<template lang="pug">
.acq-widget(v-if="mounted")
  //- Launcher
  button.acq-launcher(
    v-if="!open"
    type="button"
    :aria-label="'Abrir chat con ' + brand"
    @click="openChat")
    svg(width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
      path(d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z")

  //- Panel
  .acq-panel(v-else role="dialog" aria-label="Chat de ventas")
    .acq-head
      .acq-title
        span.acq-dot(aria-hidden="true")
        span {{ brand }}
      button.acq-close(type="button" aria-label="Cerrar chat" @click="open = false") &times;

    .acq-log(ref="log")
      .acq-msg(v-for="(m, i) in visible" :key="i" :class="'acq-' + m.role")
        | {{ m.content }}
      .acq-msg.acq-assistant.acq-typing(v-if="sending" aria-live="polite")
        span.acq-bounce
        span.acq-bounce
        span.acq-bounce
      p.acq-error(v-if="error") {{ error }}

    form.acq-form(@submit.prevent="send")
      input.acq-input(
        v-model="draft"
        :disabled="sending || closed"
        :placeholder="closed ? 'Conversación cerrada' : 'Escribe tu mensaje…'"
        maxlength="2000"
        aria-label="Mensaje")
      button.acq-send(type="submit" :disabled="sending || closed || !draft.trim()" aria-label="Enviar") →
</template>

<script>
import axios from 'axios';

// Persisted so a reload continues the same conversation instead of starting a
// fresh one and losing everything the visitor already told us.
const SESSION_KEY = 'acq_chat_session';

export default {
  name: 'V2ChatWidget',
  props: {
    brand: { type: String, default: 'DConsultores' },
    greeting: {
      type: String,
      default: '¿Qué te está costando más trabajo hoy: facturar, cobrar, o saber si un proyecto dejó dinero?',
    },
  },
  data() {
    return {
      mounted: false,
      open: false,
      sending: false,
      closed: false,
      draft: '',
      error: '',
      messages: [],
      sessionId: null,
    };
  },
  computed: {
    visible() {
      return this.messages.filter(m => m.role === 'user' || m.role === 'assistant');
    },
  },
  methods: {
    openChat() {
      this.open = true;
      if (this.messages.length === 0) {
        // Local only. It is not sent to the model, so it costs nothing and
        // cannot drift from the system prompt.
        this.messages.push({ role: 'assistant', content: this.greeting });
      }
      this.$nextTick(this.scroll);
    },
    async send() {
      const text = this.draft.trim();
      if (!text || this.sending || this.closed) return;

      this.error = '';
      this.messages.push({ role: 'user', content: text });
      this.draft = '';
      this.sending = true;
      this.$nextTick(this.scroll);

      try {
        const { data } = await axios.post('/api/acquisition/chat', {
          session_id: this.sessionId,
          message: text,
        });
        this.messages.push({ role: 'assistant', content: data.reply });
        this.closed = !!data.closed;
      } catch (e) {
        const status = e?.response?.status;
        this.error = status === 429
          ? 'Demasiados mensajes seguidos. Espera un momento, por favor.'
          : 'No pude enviar el mensaje. Intenta de nuevo o escríbenos por correo.';
      } finally {
        this.sending = false;
        this.$nextTick(this.scroll);
      }
    },
    scroll() {
      const el = this.$refs.log;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
  mounted() {
    // crypto.randomUUID needs a secure context; the fallback keeps the widget
    // working on plain http during local development.
    let id = null;
    try {
      id = window.localStorage.getItem(SESSION_KEY);
    } catch (e) { /* private mode: fall through to an in-memory session */ }

    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
          });
      try { window.localStorage.setItem(SESSION_KEY, id); } catch (e) { /* ignore */ }
    }

    this.sessionId = id;
    this.mounted = true;
  },
};
</script>

<style scoped>
.acq-widget { position: fixed; right: 20px; bottom: 20px; z-index: 9999; font-family: var(--v2-font, Inter, system-ui, sans-serif); }

.acq-launcher {
  width: 52px; height: 52px; border-radius: 50%; border: none; cursor: pointer;
  background: var(--v2-accent, #2563eb); color: #fff;
  box-shadow: 0 6px 20px rgba(0,0,0,0.28);
  display: flex; align-items: center; justify-content: center;
}
.acq-launcher:hover { filter: brightness(1.1); }

.acq-panel {
  width: 360px; max-width: calc(100vw - 32px); height: 480px; max-height: calc(100vh - 96px);
  display: flex; flex-direction: column; overflow: hidden;
  background: var(--v2-bg-surface, #161b22);
  border: 1px solid var(--v2-border, #30363d);
  border-radius: var(--v2-radius-lg, 12px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.4);
}

.acq-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-bottom: 1px solid var(--v2-border, #30363d); }
.acq-title { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: var(--v2-text-primary, #e6edf3); }
.acq-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--v2-green, #3fb950); }
.acq-close { background: none; border: none; color: var(--v2-text-secondary, #8b949e); font-size: 22px; line-height: 1; cursor: pointer; }

.acq-log { flex: 1 1 auto; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.acq-msg { max-width: 82%; padding: 9px 12px; border-radius: 12px; font-size: 13px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; }
.acq-user { align-self: flex-end; background: var(--v2-accent, #2563eb); color: #fff; border-bottom-right-radius: 4px; }
.acq-assistant { align-self: flex-start; background: var(--v2-bg-elevated, #21262d); color: var(--v2-text-primary, #e6edf3); border-bottom-left-radius: 4px; }
.acq-error { font-size: 12px; color: var(--v2-red, #f85149); margin: 4px 0 0; }

.acq-typing { display: flex; gap: 4px; align-items: center; }
.acq-bounce { width: 6px; height: 6px; border-radius: 50%; background: var(--v2-text-muted, #6e7681); animation: acq-bounce 1.2s infinite ease-in-out; }
.acq-bounce:nth-child(2) { animation-delay: 0.15s; }
.acq-bounce:nth-child(3) { animation-delay: 0.3s; }
@keyframes acq-bounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.5; } 30% { transform: translateY(-4px); opacity: 1; } }

/* Respect a visitor who has asked for less motion. */
@media (prefers-reduced-motion: reduce) { .acq-bounce { animation: none; } }

.acq-form { display: flex; gap: 8px; padding: 10px; border-top: 1px solid var(--v2-border, #30363d); }
.acq-input {
  flex: 1 1 auto; width: 1%; min-width: 0; padding: 9px 11px; font-size: 13px;
  color: var(--v2-text-primary, #e6edf3); background: var(--v2-bg-elevated, #21262d);
  border: 1px solid var(--v2-border, #30363d); border-radius: var(--v2-radius, 6px); outline: none;
}
.acq-input:focus { border-color: var(--v2-border-focus, #58a6ff); box-shadow: 0 0 0 3px rgba(88,166,255,0.15); }
.acq-send { flex: 0 0 auto; padding: 0 14px; border: none; border-radius: var(--v2-radius, 6px); background: var(--v2-accent, #2563eb); color: #fff; font-size: 16px; cursor: pointer; }
.acq-send:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
